import { createRef, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import CircleIcon from '@mui/icons-material/Circle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	Button,
	Checkbox,
	Collapse,
	Icon,
	IconButton,
	LinearProgress,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	menuItemClasses,
	Table as MuiTable,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	Typography,
	typographyClasses
} from '@mui/material';
import { Box, styled } from '@mui/system';
import queryString from 'query-string';

import { useFiles } from 'Lib/components/common/table/filesTable/context';
import PortalTypes from 'Lib/constants/app/portalTypes';
import { isPath } from 'Lib/utilities/files';
import filterParameters from 'Lib/utilities/filterParameters';
import getIn from 'Lib/utilities/getIn';
import { screenSize } from 'Lib/utilities/screenSize';

import TableActions from './actions';
import EmptyView, { EmptyFilesView } from './EmptyView';
import { ListTableToolbar } from './index';
import Skeleton from './Skeleton';

const SORT_DIRECTIONS = { ASCENDING: 'asc', DESCENDING: 'desc' };

const HeaderText = styled(Typography)(({ theme }) => ({
	fontSize: theme.typography.pxToRem(16),
	fontWeight: theme.typography.fontWeightMedium,
	width: 'max-content',
	letterSpacing: 'unset'
}));

const Row = ({ areAllExpanded, density, item, itemIndex, selected, striped, handleCheckboxSelect, onRowClick, variant, ...rest }) => {
	useEffect(() => {
		setExpanded(areAllExpanded);
	}, [areAllExpanded]);

	const getRowStyles = () => {
		const onHover = {
			bgcolor: 'common.lightblue',
			...(isFirstRow && {
				[`& .${tableCellClasses.root}`]: {
					borderTopColor: '#e0e0e0 !important',
					borderTop: 1
				}
			})
		};

		return {
			cursor: onRowClick || rest.to || rest.externalTo ? 'pointer' : 'inherit',
			'&:last-child td': {
				borderBottom: 'none'
			},
			position: 'relative',
			...(striped &&
				itemIndex % 2 == 0 && {
					bgcolor: 'common.darkgray'
				}),
			'&:hover': onHover,
			'&.MuiTableRow-root': {
				...(isFirstRow &&
					variant != 'tab-table' && {
						'&:not(:hover)': {
							[`& .${tableCellClasses.root}`]: {
								borderTopColor: 'white !important',
								borderTop: 1
							}
						}
					})
			},
			'&.MuiTableRow-root.Mui-selected': {
				backgroundColor: 'transparent',
				'&:hover': onHover
			},
			...(expanded && {
				bgcolor: 'common.lightblue',
				...(isFirstRow && { borderTopColor: '#e0e0e0 !important', borderTop: 1 }),
				[`& .${tableCellClasses.root}`]: {
					borderBottom: 'unset'
				}
			})
		};
	};

	const history = useHistory();

	const [isDragging, setIsDragging] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const isFirstRow = itemIndex == 0;

	const tableItemID = rest.checkable ? getIn(item, rest.checkable(item)) : null;
	const isItemSelected = rest.checkable ? !!selected.find(s => getIn(s, rest.checkable(s)) == tableItemID) : null;

	const { portalTypeID } = useFiles?.() ?? {};
	const isManagerPortal = portalTypeID === PortalTypes.Manager;

	const toggleExpanded = useCallback(e => {
		e.stopPropagation();
		setExpanded(prev => !prev);
	}, []);

	const handleDragStart = useCallback(
		event => {
			setIsDragging(true);
			const rowElement = event.currentTarget;
			const clonedRow = rowElement.cloneNode(true);

			Object.assign(clonedRow.style, {
				width: `${rowElement.clientWidth}px`,
				backgroundColor: 'white',
				position: 'absolute',
				top: '-1000px',
				zIndex: '-1',
				border: '2px dashed #1473CC',
				boxShadow: '8px 24px 24px 4px rgba(0, 0, 0, 0.25)'
			});

			document.body.appendChild(clonedRow);
			event.dataTransfer.setDragImage(clonedRow, 0, 0);
			event.dataTransfer.setData('text/plain', JSON.stringify(item));

			setTimeout(() => document.body.removeChild(clonedRow), 0);

			if (rest.onRowDragStart) rest.onRowDragStart(item, itemIndex);
		},
		[item, itemIndex, rest.onRowDragStart]
	);

	const handleDragOver = useCallback(
		event => {
			event.preventDefault();
			if (isPath(item)) event.dataTransfer.dropEffect = 'move';

			rest.onRowDragOver?.(item, itemIndex);
		},
		[item, itemIndex, rest.onRowDragOver]
	);

	const handleDragLeave = useCallback(() => {
		if (rest.onRowDragLeave) rest.onRowDragLeave();
	}, [rest.onRowDragLeave]);

	const handleDrop = useCallback(
		event => {
			event.preventDefault();
			event.stopPropagation();

			try {
				const droppedItem = JSON.parse(event.dataTransfer.getData('text/plain'));
				// Check if the dropped item is from within the app
				if (droppedItem && typeof droppedItem === 'object' && Object.keys(droppedItem).length > 0) {
					rest.onRowDrop?.(item, droppedItem);
				} else {
					// Handle drop from user's PC
					const files = Array.from(event.dataTransfer.files);
					if (files.length > 0) rest.onDropFiles?.(files);
				}
			} catch (error) {
				// Handle drop from user's PC
				const files = Array.from(event.dataTransfer.files);

				if (files.length > 0) rest.onDropFiles?.(files);
			}
		},
		[item, rest.onRowDrop, rest.onDropFiles]
	);

	const handleDragEnd = useCallback(() => {
		setIsDragging(false);
		rest.onRowDragEnd?.(item);
	}, [item, rest.onRowDragEnd]);

	const handleRowClick = useCallback(() => {
		if (onRowClick) onRowClick(item, itemIndex);
		else if (rest.to) history.push(rest.to(item));
		else if (rest.externalTo) window.open(rest.externalTo(item), '_blank');
	}, [onRowClick, item, itemIndex, rest.to, rest.externalTo, history]);

	const rowProps = {
		onClick: handleRowClick,
		draggable: rest.files && isManagerPortal,
		onDragStart: handleDragStart,
		onDragOver: handleDragOver,
		onDragLeave: handleDragLeave,
		onDrop: handleDrop,
		onDragEnd: handleDragEnd,
		sx: getRowStyles(expanded, striped, itemIndex === 0, isDragging),
		...(rest.checkable && {
			role: 'checkbox',
			'aria-checked': isItemSelected,
			selected: isItemSelected
		})
	};

	return (
		<>
			{item.moving ? (
				<TableRow {...rowProps}>
					<TableCell colSpan={12}>
						<Box px={12} gap={2} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
							<Typography>{`Moving ${item.name || ''}...`}</Typography>
							<LinearProgress />
						</Box>
					</TableCell>
				</TableRow>
			) : (
				<TableRow {...rowProps}>
					{/* EXPANDED BUTTON */}
					{!rest.loading && rest.tableExpanded && (
						<TableCell
							sx={theme => ({
								py: 0,
								px: 1,
								...(expanded && {
									borderLeft: `4px solid ${theme.palette.common.darkblue}`,
									pl: 0
								})
							})}>
							<IconButton aria-label="expand row" onClick={toggleExpanded} size="small">
								{expanded ? 'expand_less' : 'expand_more'}
							</IconButton>
						</TableCell>
					)}
					{/* CHECKBOX */}
					{!rest.loading && rest.checkable && (
						<TableCell padding="checkbox">
							<Checkbox
								color="success"
								checked={isItemSelected}
								onClick={event =>
									handleCheckboxSelect(event, {
										...item,
										index: itemIndex
									})
								}
							/>
						</TableCell>
					)}
					{/* COLUMNS */}
					{rest.rows.map(
						(column, columnIndex) =>
							column.visible && (
								<TableCell
									key={column.order ?? columnIndex}
									sx={theme => ({
										py: density,
										px: 4,
										width: 170,
										...column.sx?.(theme)
									})}>
									{column.render(item, itemIndex)}
								</TableCell>
							)
					)}
					{/* ACTIONS */}
					<TableActions {...rest} data={{ ...item, index: itemIndex }} />
				</TableRow>
			)}
			{/* EXPANDED TABLE ROW */}
			{rest.tableExpanded && (
				<TableRow
					sx={{
						bgcolor: 'common.lightblue',
						cursor: rest.to ? 'pointer' : 'inherit'
					}}
					// TODO: Not sure if we want a click event on the expanded row so users are able to click things like email, phone, map, etc.
					// onClick={() => rest.to && history.push(rest.to(item))}
					{...(rest.checkable && { selected: isItemSelected })}>
					<TableCell
						colSpan={10}
						sx={theme => ({
							py: 0,
							...(expanded && {
								borderLeft: 4,
								borderLeftColor: `${theme.palette.common.darkblue} !important`,
								borderTop: theme => `1px solid ${theme.palette.common.lightgray}`
							})
						})}>
						<Collapse in={expanded} timeout="auto" unmountOnExit>
							{rest.tableExpanded(item)}
						</Collapse>
					</TableCell>
				</TableRow>
			)}
		</>
	);
};

export default function ListTable({ files, selected = [], ...props }) {
	const tableRef = createRef();
	const dispatch = useDispatch();
	const history = useHistory();
	const { density, striped, columns, rows } = useSelector(state => state.ui.table);

	const { actions } = props.common;

	const { orderBy, page } = queryString.parse(history.location.search);

	const [areAllExpanded, setAreAllExpanded] = useState(false);
	const [sort, setSort] = useState({});
	const [loadingData, setLoadingData] = useState(true);
	const [anchorElement, setAnchorElement] = useState(null);

	const { parsed } = filterParameters();
	const isEmpty = props.data?.length === 0;

	useEffect(() => {
		const [key, direction] = orderBy?.split('|') ?? [];

		setSort({ key, direction });

		dispatch(
			actions.setTableInformation({
				columns: tableColumns,
				rows: tableRows
			})
		);

		setLoadingData(false);

		return () => {
			dispatch(
				actions.setTableInformation({
					columns: [],
					rows: []
				})
			);
		};
	}, []);

	useEffect(() => {
		tableRef.current && tableRef.current.scroll(0, 0);
	}, [page]);

	const tableColumns = props.columns.map((column, index) => ({
		...column,
		order: index + 1,
		visible: column.visible ?? true
	}));

	const tableRows = props.rows.map((row, index) => ({
		...row,
		order: index + 1,
		visible: props.columns[index]?.visible ?? true
	}));

	const handleSort = key => {
		let updatedSearchParams = {};

		// First sorted direction will be ascending
		if (!sort.direction || sort.direction === SORT_DIRECTIONS.DESCENDING || key !== sort.key) {
			updatedSearchParams = {
				...parsed,
				orderBy: `${key}|${SORT_DIRECTIONS.ASCENDING}`
			};

			setSort({
				key,
				direction: SORT_DIRECTIONS.ASCENDING
			});

			history.replace({
				search: queryString.stringify(updatedSearchParams)
			});
		} else {
			updatedSearchParams = {
				...parsed,
				orderBy: `${key}|${SORT_DIRECTIONS.DESCENDING}`
			};

			setSort({
				key,
				direction: SORT_DIRECTIONS.DESCENDING
			});

			history.replace({
				search: queryString.stringify(updatedSearchParams)
			});
		}

		props.fetch(updatedSearchParams);
	};

	const Header = ({ value }) => {
		if (typeof value === 'string') return value;
		else if (typeof value === 'object') return value.label;
	};

	const { mobile } = screenSize();

	const tableContainerStyle = {
		bgcolor: 'common.white',
		borderRadius: 1,
		height: (files || selected.length) && !mobile ? 'calc(100% - 56px)' : '100%',
		mx: { sm: 0 },
		width: 'unset',
		[`& .${tableCellClasses.head}`]: { [`& .${typographyClasses.root}`]: { fontWeight: theme => theme.typography.fontWeightMedium } },
		...(mobile && { border: 'unset' })
	};

	return (
		<Box
			component="section"
			aria-label="Results list"
			sx={
				mobile
					? { height: '100%', ...props.sx }
					: {
							border: '1px solid #DDD',
							borderRadius: 1,
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							position: 'relative',
							overflow: props.selectedActions ? 'hidden' : 'auto',
							...props.sx
						}
			}>
			<TableContainer ref={tableRef} elevation={0} sx={tableContainerStyle}>
				<MuiTable stickyHeader variant={props.variant} sx={() => isEmpty && { height: '100%' }}>
					<TableHead>
						<TableRow
							sx={{
								[`& .${tableCellClasses.root}`]: {
									bgcolor: (props.variant == 'tab-table' || !striped) && 'common.lightblue',
									borderBottom: 'unset'
								}
							}}>
							{!props.loading && props.tableExpanded && (
								<TableCell sx={{ py: 0, px: 1, width: '0%' }}>
									<IconButton aria-label="expand row" onClick={() => setAreAllExpanded(!areAllExpanded)} sx={{ py: 0 }}>
										{areAllExpanded ? 'expand_less' : 'expand_more'}
									</IconButton>
								</TableCell>
							)}
							{props.checkable && (
								<TableCell padding="checkbox">
									<Checkbox
										disabled={props.loading}
										color="success"
										indeterminate={!props.areAllSelected && props.areSomeSelected}
										checked={props.areAllSelected}
										onChange={props.handleCheckboxSelectAll}
									/>
								</TableCell>
							)}
							{columns.map((column, index) => {
								if (!column.visible) return null;
								const sortableTableHeader = column.sortBy;
								// Add extra colSpan if Table Actions are present
								const colSpan = columns.length - 1 == index ? 2 : 1;

								const SortIcon = () => {
									if (sort.key == sortableTableHeader && sort.direction === SORT_DIRECTIONS.ASCENDING) return <Icon>arrow_upward</Icon>;
									else if (sort.key == sortableTableHeader && sort.direction === SORT_DIRECTIONS.DESCENDING) return <Icon>arrow_downward</Icon>;
									else return <Icon>swap_vert</Icon>;
								};

								return (
									<TableCell
										key={index}
										colSpan={colSpan}
										onClick={() => (sortableTableHeader ? handleSort(sortableTableHeader) : null)}
										sx={theme => ({
											zIndex: 3,
											...(props.loading && {
												py: 5
											}),
											...(column.sortBy &&
												!props.loading && {
													'&:hover': {
														cursor: 'pointer',
														bgcolor: 'hover.main'
													}
												}),
											...column.sx?.(theme)
										})}>
										{column.filterBy ? (
											<>
												<Button
													variant="text"
													aria-label={`filter list by ${column.filterBy}`}
													onClick={event => setAnchorElement(event.currentTarget)}
													endIcon={<ExpandMoreIcon size="small" />}
													sx={{
														color: 'common.black',
														borderRadius: 3,
														bgcolor: anchorElement ? 'common.lightgray' : 'inherit'
													}}>
													<HeaderText>
														<Header value={column} />
													</HeaderText>
												</Button>
												<Menu
													anchorEl={anchorElement}
													open={Boolean(anchorElement)}
													onClose={() => setAnchorElement(null)}
													anchorOrigin={{
														vertical: 'bottom',
														horizontal: 'center'
													}}
													transformOrigin={{
														vertical: 'top',
														horizontal: 'center'
													}}
													sx={{
														[`& .${menuItemClasses.root}`]: { py: theme => theme.spacing(3) }
													}}>
													<MenuItem
														onClick={() => {
															history.push('');
															props.fetch();

															setAnchorElement(null);
														}}>
														<ListItemIcon>
															<Icon>clear_all</Icon>
														</ListItemIcon>
														<ListItemText>Clear</ListItemText>
													</MenuItem>
													{column.options.map(option => {
														const handleClick = () => {
															if (option.onClick) {
																option.onClick();
															} else {
																const updatedSearchParams = {
																	[column.filterBy]: option.value
																};

																history.push({
																	search: queryString.stringify(updatedSearchParams)
																});

																props.fetch(updatedSearchParams);
															}

															setAnchorElement(null);
														};

														return (
															<MenuItem key={option.label} onClick={handleClick}>
																<ListItemIcon>
																	<CircleIcon
																		color={option.color}
																		sx={{
																			height: 16,
																			width: 16
																		}}
																	/>
																</ListItemIcon>
																<ListItemText>{option.label}</ListItemText>
															</MenuItem>
														);
													})}
												</Menu>
											</>
										) : (
											<Box
												sx={{
													display: 'flex',
													alignItems: 'center'
												}}>
												<HeaderText component="span" sx={{ mr: 1 }}>
													<Header value={column} />
												</HeaderText>
												{column.sortBy && <SortIcon />}
											</Box>
										)}
									</TableCell>
								);
							})}
						</TableRow>
					</TableHead>
					{!isEmpty || props.loading || loadingData ? (
						<TableBody>
							{props.loading || loadingData ? (
								// Get length of visible rows so we only show loading state for what is visible
								<Skeleton hasSelect={props.checkable} count={rows.filter(row => row.visible).length} />
							) : (
								props.data?.map((item, itemIndex) => (
									<Row
										{...props}
										files={files}
										key={itemIndex}
										areAllExpanded={areAllExpanded}
										density={files ? 2 : density}
										item={item}
										itemIndex={itemIndex}
										selected={selected}
										striped={files ? false : striped}
										rows={rows}
										handleCheckboxSelect={props.handleCheckboxSelect}
									/>
								))
							)}
						</TableBody>
					) : (
						<TableBody sx={{ height: '100%' }}>
							<TableRow>
								<TableCell align="center" colSpan={props.columns.length + 1}>
									{files && <EmptyFilesView title={props.breadcrumbs.length > 1 ? 'This folder is empty.' : props.emptyMessage} />}
									{!files && <EmptyView grow title={props.emptyMessage} iconFlair={props.emptyFlair} iconName={props.emptyIcon} />}
								</TableCell>
							</TableRow>
						</TableBody>
					)}

					{!props.loading && props.tableFooter && (
						<TableFooter
							sx={{
								bottom: 0,
								position: 'sticky',
								bgcolor: 'common.lightblue'
							}}>
							<TableRow>
								<TableCell align="right" {...props.tableFooter.props} colSpan={props.columns.length + 1}>
									{props.tableFooter.render()}
								</TableCell>
							</TableRow>
						</TableFooter>
					)}
					{!props.loading && props.footer && (
						<TableFooter
							sx={{
								bottom: 0,
								position: 'sticky',
								bgcolor: 'common.lightblue'
							}}>
							<TableRow>
								{props.footer.map((column, index) => (
									<TableCell key={index} sx={{ border: 'none' }}>
										{column.render()}
									</TableCell>
								))}
							</TableRow>
						</TableFooter>
					)}
				</MuiTable>
			</TableContainer>
			<ListTableToolbar loading={props.loading} files={files} count={props.data.length} selected={selected.length} actions={props.selectedActions} />
		</Box>
	);
}
